import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Robot, PaperPlaneTilt, User, Sparkle, ChatCircleDots,
  ArrowsClockwise, BookBookmark, MagnifyingGlass, X
} from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';

const HELP_BUDDY_AVATAR = (props) => <Sparkle {...props} weight="duotone" />;

const TABS = [
  { key: 'chat',     label: 'Chat',     icon: ChatCircleDots },
  { key: 'glossary', label: 'Glossary', icon: BookBookmark },
];

const Help = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('chat');

  // ===== Chat state =====
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // ===== Glossary state =====
  const [glossary, setGlossary] = useState([]);
  const [glossaryLoading, setGlossaryLoading] = useState(false);
  const [glossaryQuery, setGlossaryQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages, isTyping]);

  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: `Hey ${user?.username || 'there'}! 👋 I'm Help Buddy — your friendly investing companion.\n\nI can help with:\n📊 Trading questions\n💰 Account help\n📚 Stock market basics\n🎯 App features\n\n💡 Tip: Switch to the Glossary tab to look up terms like P/E ratio, Net-net, Circle of Competence, and more!`,
      timestamp: new Date().toISOString(),
    }]);
  }, [user]);

  useEffect(() => {
    if (tab !== 'glossary' || glossary.length) return;
    setGlossaryLoading(true);
    api.get('/learning/glossary')
      .then((r) => setGlossary(r.data.terms || []))
      .catch(() => toast.error('Failed to load glossary'))
      .finally(() => setGlossaryLoading(false));
  }, [tab, glossary.length]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;
    const userMessage = { role: 'user', content: inputMessage, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setIsTyping(true);
    try {
      const response = await api.post('/chat/send', { message: inputMessage, session_id: sessionId });
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.message, timestamp: response.data.timestamp }]);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  };

  const handleQuickQuestion = (q) => setInputMessage(q);

  const startNewChat = async () => {
    try {
      const response = await api.post('/chat/new-session');
      setSessionId(response.data.session_id);
      setMessages([{
        role: 'assistant',
        content: `Fresh chat started! How can Help Buddy assist you today?`,
        timestamp: new Date().toISOString(),
      }]);
      toast.success('New chat session started');
    } catch (error) {
      toast.error('Failed to start new chat');
    }
  };

  const quickQuestions = [
    'How do I buy a stock?',
    'What is P/E ratio?',
    'Explain Net-net stocks',
    'How does Master rank work?',
  ];

  // Glossary derived data
  const categories = useMemo(() => {
    const set = new Set(glossary.map(t => t.category).filter(Boolean));
    return ['All', ...Array.from(set).sort()];
  }, [glossary]);

  const filteredGlossary = useMemo(() => {
    const q = glossaryQuery.trim().toLowerCase();
    return glossary.filter(t => {
      if (activeCategory !== 'All' && t.category !== activeCategory) return false;
      if (!q) return true;
      return (
        t.term.toLowerCase().includes(q) ||
        t.definition.toLowerCase().includes(q) ||
        (t.example || '').toLowerCase().includes(q)
      );
    });
  }, [glossary, glossaryQuery, activeCategory]);

  return (
    <div className="min-h-screen noise-bg" data-testid="help-buddy-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-gold-light flex items-center justify-center">
                <HELP_BUDDY_AVATAR size={32} className="text-black" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white mb-1">Help Buddy</h1>
                <p className="text-base text-zinc-400">Your investing sidekick — chat + glossary, all in one place.</p>
              </div>
            </div>
            {tab === 'chat' && (
              <button
                onClick={startNewChat}
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="help-new-chat"
              >
                <ArrowsClockwise size={18} className="text-gold" />
                <span className="text-sm text-white">New Chat</span>
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-white/5">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-bold transition-colors relative ${
                    active ? 'text-gold' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`help-tab-${t.key}`}
                >
                  <Icon size={18} weight={active ? 'fill' : 'regular'} />
                  {t.label}
                  {active && (
                    <motion.div
                      layoutId="help-tab-underline"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* TAB: CHAT */}
        {tab === 'chat' && (
          <motion.div
            key="chat"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card p-5 h-[600px] flex flex-col"
            data-testid="help-chat-panel"
          >
            <div className="flex-1 overflow-y-auto mb-4 space-y-4 pr-2">
              <AnimatePresence>
                {messages.map((msg, index) => (
                  <motion.div
                    key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
                      msg.role === 'user' ? 'bg-blue-500/20' : 'bg-gradient-to-br from-gold/20 to-gold-light/20'
                    }`}>
                      {msg.role === 'user'
                        ? <User size={20} className="text-blue-400" weight="duotone" />
                        : <Sparkle size={20} className="text-gold" weight="duotone" />}
                    </div>
                    <div className={`max-w-[75%] ${
                      msg.role === 'user' ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-white/5 border border-white/10'
                    } rounded-2xl p-4`}>
                      <p className="text-white text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>
                      <p className="text-xs text-zinc-500 mt-2">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {isTyping && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                  <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-gold/20 to-gold-light/20">
                    <Sparkle size={20} className="text-gold animate-pulse" weight="duotone" />
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                    <div className="flex gap-2">
                      <div className="w-2 h-2 bg-gold rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 1 && (
              <div className="mb-3">
                <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wide">Quick questions</p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.map((q, i) => (
                    <button
                      key={i} onClick={() => handleQuickQuestion(q)}
                      className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-zinc-300 transition-colors"
                      data-testid={`help-quick-q-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={sendMessage} className="flex gap-2">
              <input
                type="text" value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask Help Buddy anything..."
                disabled={loading}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-gold/50 disabled:opacity-50"
                data-testid="help-chat-input"
              />
              <button
                type="submit" disabled={loading || !inputMessage.trim()}
                className="px-5 py-3 bg-gradient-to-r from-gold to-gold-light text-black font-bold rounded-xl hover:shadow-lg hover:shadow-gold/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="help-chat-send"
              >
                <PaperPlaneTilt size={18} weight="fill" />
              </button>
            </form>
          </motion.div>
        )}

        {/* TAB: GLOSSARY */}
        {tab === 'glossary' && (
          <motion.div
            key="glossary"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="help-glossary-panel"
          >
            <div className="glass-card p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <BookBookmark size={20} weight="fill" className="text-gold" />
                <h2 className="text-lg font-unbounded font-bold text-white">Investing Glossary</h2>
                <span className="text-xs text-zinc-500 ml-1">{glossary.length} terms</span>
              </div>
              <div className="relative mb-3">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={glossaryQuery}
                  onChange={(e) => setGlossaryQuery(e.target.value)}
                  placeholder="Search P/E, Net-net, Moat, Circle of Competence…"
                  className="pl-10 pr-10 bg-black/40 border-white/10 text-white"
                  data-testid="glossary-search"
                />
                {glossaryQuery && (
                  <button
                    onClick={() => setGlossaryQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    data-testid="glossary-clear"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${
                      activeCategory === cat
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
                        : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`glossary-cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {glossaryLoading ? (
              <p className="text-center text-zinc-400 py-12">Loading glossary...</p>
            ) : filteredGlossary.length === 0 ? (
              <div className="glass-card p-10 text-center">
                <p className="text-zinc-400">No terms match "{glossaryQuery}". Try another keyword.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="glossary-list">
                {filteredGlossary.map((t, i) => (
                  <motion.div
                    key={t.term}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.6) }}
                    className="glass-card p-5 hover:border-gold/30 border border-transparent transition-colors"
                    data-testid={`glossary-term-${t.term.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-unbounded font-bold text-white">{t.term}</h3>
                      {t.category && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30 whitespace-nowrap">
                          {t.category}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed mb-2">{t.definition}</p>
                    {t.example && (
                      <p className="text-xs text-zinc-500 italic">
                        <span className="text-zinc-400 font-bold not-italic">Example: </span>
                        {t.example}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Help;
